import { t } from 'i18next';
import type { Leave, VacationStatus } from '@/types/api';

/** 1일 = 8시간 (근태 기준) */
export const WORK_HOURS_PER_DAY = 8;
/** 1일 = 8시간 = 480분 */
export const WORK_MINUTES_PER_DAY = WORK_HOURS_PER_DAY * 60;

// 숫자 + 단위(일/시간)를 현재 언어로 포맷 (en: '3 days', zh: '3天')
const dayUnit = (value: number) => t('MYPAGE.{{value}}일', { value });
const hourUnit = (value: number) => t('MYPAGE.{{value}}시간', { value });

/** 휴가 현황 API의 일·시·분 단위 기간 객체 */
export type LeaveDuration = {
  day: number;
  hour: number;
  minute: number;
};

/** {day, hour, minute} 기간을 분(minute) 합계로 환산 (1일 = 480분) */
export const durationToMinutes = (d?: LeaveDuration): number =>
  d ? d.day * WORK_MINUTES_PER_DAY + d.hour * 60 + d.minute : 0;

/**
 * 분(minute) 단위 값을 'N일 M시간' 형태로 포맷한다. (1일 = 8시간)
 * - 1시간 미만 단위는 시간 소수로 표기한다. (30분 → 0.5시간)
 * - 음수 값은 항상 '-' 부호를 붙인다. (잔여 휴가가 마이너스인 경우)
 * - signed=true 이면 양수에도 '+' 부호를 붙인다.
 *
 * 예) 1920 → '4일', 1080 → '2일 2시간', 330 → '5.5시간', 0 → '0일', -480 → '-1일'
 */
export const formatLeaveMinutes = (
  totalMinutes: number,
  signed = false,
): string => {
  // 음수는 항상 '-' 표시, 양수는 signed일 때만 '+' 표시
  const sign = totalMinutes < 0 ? '-' : signed && totalMinutes > 0 ? '+' : '';
  const abs = Math.abs(totalMinutes);
  const day = Math.floor(abs / WORK_MINUTES_PER_DAY);
  const remMinutes = abs - day * WORK_MINUTES_PER_DAY;
  // 남은 분을 시간 단위(소수 허용)로 변환하고 부동소수 오차 제거
  const hour = Math.round((remMinutes / 60) * 100) / 100;

  let label: string;
  if (day > 0 && hour > 0) label = `${dayUnit(day)} ${hourUnit(hour)}`;
  else if (day > 0) label = dayUnit(day);
  else if (hour > 0) label = hourUnit(hour);
  else label = dayUnit(0);

  return `${sign}${label}`;
};

/**
 * 연차/휴가의 fractional day 값을 'N일 M시간' 형태로 포맷한다. (1일 = 8시간)
 * - 반차(0.5일)·시간차로 인해 시간 단위에 소수(예: 5.5시간)가 생길 수 있어 반올림하지 않고 유지한다.
 * - signed=true 이면 부호(+/-)를 붙인다.
 *
 * 예) 12.25 → '12일 2시간', 1.75 → '1일 6시간', 10.6875 → '10일 5.5시간', 11 → '11일'
 */
export const formatLeaveDays = (value: number, signed = false): string => {
  const abs = Math.abs(value);
  const days = Math.floor(abs);
  // 0.5시간 단위까지 유지 (부동소수 오차 제거)
  const hours = Math.round((abs - days) * WORK_HOURS_PER_DAY * 10) / 10;
  const sign = signed ? (value > 0 ? '+' : value < 0 ? '-' : '') : '';

  let label: string;
  if (days > 0 && hours > 0) label = `${dayUnit(days)} ${hourUnit(hours)}`;
  else if (days > 0) label = dayUnit(days);
  else if (hours > 0) label = hourUnit(hours);
  else label = dayUnit(0);

  return `${sign}${label}`;
};

// 일·시·분 단위 기간(grantedDuration/usedDuration)을 분으로 환산.
// 신규 API에 duration 이 없으면 일 단위 값(granted/used)으로 폴백한다.
export const leaveGrantedMinutes = (leave: Leave): number =>
  durationToMinutes(
    leave.grantedDuration ?? { day: leave.granted ?? 0, hour: 0, minute: 0 },
  );
export const leaveUsedMinutes = (leave: Leave): number =>
  durationToMinutes(
    leave.usedDuration ?? { day: leave.used ?? 0, hour: 0, minute: 0 },
  );

/** 부여 휴가 총합(분). 0 이면 임원(무제한) 으로 간주 */
export const vacationGrantedMinutes = (data: VacationStatus): number =>
  leaveGrantedMinutes(data.monthlyLeave) +
  leaveGrantedMinutes(data.annualLeave);

/** 휴가 현황의 잔여 휴가(분). 월차+연차 부여 − (사용 + 전년 초과). 1일 = 480분 */
export const vacationRemainingMinutes = (data: VacationStatus): number => {
  const { monthlyLeave, annualLeave, overage = 0, overageDuration } = data;
  const usedMinutes =
    leaveUsedMinutes(monthlyLeave) + leaveUsedMinutes(annualLeave);
  const overageMinutes = durationToMinutes(
    overageDuration ?? { day: overage, hour: 0, minute: 0 },
  );
  return vacationGrantedMinutes(data) - (usedMinutes + overageMinutes);
};
